import { Box, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { dateUtils } from "../../../../../../utils/DateUtils";
import { withMultipleQueryData } from "../../../../../../common/withQueryData";
import PaymentsList from "./PaymentsList";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { EditIcon } from "../../../../../../common/icons/Icons";
import ItemPriceEditor from "./ItemPriceEditor";
import { useReservationsData } from "../../../../../../common/hooks/useReservationsData";
import { usePlansData } from "../../../../../../common/hooks/useRatePlansData";
import { usePaymentsData } from "../../../../../../common/hooks/usePaymentsData";
import { useRoomsData } from "../../../../../../common/hooks/useRoomsData";
import { useEditableArray } from "../../../../../../common/hooks/useEditableArray";

const ENUM_ITEM_TYPES = {
    ROOM: 'ROOM'
}

const PaymentDetails = forwardRef((props, ref) => {
    const { reservation, propertyId } = props;

    const { queryResults: roomQuery } = useRoomsData(propertyId, reservation.roomId);
    const { queryResults: paymentsQuery } = usePaymentsData(propertyId, reservation.id);
    const { queryResults: plansQuery } = usePlansData(propertyId);

    return (
        <PaymentDetailsContentWithMultipleQueryData
            queryResults={{ room: roomQuery, payments: paymentsQuery, plans: plansQuery }}
            listRef={ref}
            {...props}
        />
    );
});

// Pure content component: receives fully-loaded queryResults and renders child components.
const PaymentDetailsContent = ({ queryResults, reloadAll, listRef, reservation, editing, onChange, propertyId, resetChildrenChanged }) => {
    const room = queryResults.room.data;
    const payments = queryResults.payments.data;
    const plans = queryResults.plans.data;

    const onTotalPriceChange = (price, planId) => {
        onChange([
            { id: 'totalPrice', value: price, valid: true },
            { id: 'planId', value: planId, valid: true }
        ]);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <PaymentTable
                reservation={reservation}
                room={room}
                editing={editing}
                onTotalPriceChange={onTotalPriceChange}
                plans={plans}
                propertyId={propertyId}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100px', position: 'relative' }}>
                <PaymentsListEditable
                    payments={payments}
                    reloadPayments={queryResults.payments.refetch}
                    editable={editing}
                    reservation={reservation}
                    propertyId={propertyId}
                    listRef={listRef}
                    onChange={onChange}
                    onReset={resetChildrenChanged}
                />
            </Box>
        </Box>
    );
};

// Pure payment table: owns editedItem state and renders ItemPriceEditor directly with plans.
const PaymentTable = ({ reservation, room, editing, onTotalPriceChange, plans, propertyId }) => {
    const { checkin, checkout } = reservation;
    const [editedItem, setEditedItem] = useState(null);

    const lengthOfStay = dateUtils.getLengthOfStay(checkin, checkout);

    const rows = [
        { name: room.name, price: reservation.totalPrice, planId: reservation.planId, type: ENUM_ITEM_TYPES.ROOM }
    ];

    const totalPrice = rows.reduce((total, row) => total + row.price, 0);

    const handleSaveItemPrice = (item) => {
        onTotalPriceChange(item.price, item.planId);
        setEditedItem(null);
    };

    return <>
        <Table
            sx={{
                minWidth: 350,
                '& .MuiTableCell-root': {
                    fontSize: '1rem'
                }
            }}
            aria-label="simple table"
        >
            <TableBody>
                {rows.map((row) => (
                    <TableRow
                        key={row.name}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                        <TableCell component="th" scope="row">
                            {row.name}
                            <Typography sx={{ display: 'block' }} variant='caption'>{`${lengthOfStay} nights`}</Typography>
                        </TableCell>
                        <TableCell component="th" scope="row" sx={{ float: 'right', border: 'none' }}>
                            {editing && <EditIcon title='Edit item price' onClick={() => setEditedItem(row)} />}
                        </TableCell>
                        <TableCell align="right" sx={{ width: '150px' }}>
                            {`PLN ${row.price}`}
                            <Typography sx={{ display: 'block' }} variant='caption'>{`Avg per night: PLN ${(row.price / lengthOfStay).toFixed(2)}`}</Typography>
                        </TableCell>
                    </TableRow>
                ))}
                <TableRow
                    key={'total'}
                    sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        borderTop: '1.5px solid black',
                    }}
                >
                    <TableCell />
                    <TableCell component="th" scope="row" sx={{ fontWeight: 700, textAlign: 'right' }}>
                        Total
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, width: '150px' }}>{`PLN ${totalPrice}`}</TableCell>
                </TableRow>
            </TableBody>
        </Table>
        {editedItem && <ItemPriceEditor
            item={editedItem}
            plans={plans}
            onSave={handleSaveItemPrice}
            onClose={() => setEditedItem(null)}
            reservation={reservation}
            propertyId={propertyId}
        />}
    </>;
};

// Editable payments list: manages local editable array and exposes save/reset/validate via listRef.
const PaymentsListEditable = ({ payments, reloadPayments, editable, reservation, propertyId, listRef, onChange, onReset }) => {
    const [editablePayments, setEditablePayments, isPaymentsChanged, resetEditablePayments] = useEditableArray(payments);
    const { updateReservationPayments } = useReservationsData(propertyId, reservation.id);

    const save = async () => {
        const newPayments = editablePayments.map(({ id, ...rest }) => {
            if (typeof id === 'string' && id.startsWith('new')) return rest;
            return { id, ...rest };
        });
        await updateReservationPayments(reservation.id, newPayments);
    };

    const reset = () => {
        resetEditablePayments();
        onReset();
    };

    const isTotalsValid = useCallback((paymentsToCheck) => {
        return paymentsToCheck.reduce((acc, p) => acc + p.amountDue, 0) === reservation.totalPrice;
    }, [reservation.totalPrice]);

    const validate = () => onChange('payments', undefined, isTotalsValid(editablePayments));

    const updatePaymentsList = (newPayments) => {
        setEditablePayments(newPayments);
        onChange('payments', true, isTotalsValid(newPayments));
    };

    const onCreatePayment = (newPayment) => updatePaymentsList([...editablePayments, newPayment]);
    const onUpdatePayment = (updated) => updatePaymentsList(editablePayments.map(p => p.id === updated.id ? updated : p));
    const onDeletePayment = (paymentId) => updatePaymentsList(editablePayments.filter(p => p.id !== paymentId));

    useImperativeHandle(listRef, () => ({
        save,
        reset,
        reload: reloadPayments,
        validate
    }));

    return (
        <PaymentsList
            payments={editablePayments}
            editable={editable}
            reservation={reservation}
            propertyId={propertyId}
            onCreated={onCreatePayment}
            onUpdated={onUpdatePayment}
            onDeleted={onDeletePayment}
            onReset={reset}
            expectedTotals={{ amountDue: reservation.totalPrice }}
            disableResetButton={!isPaymentsChanged}
        />
    );
};

const PaymentDetailsContentWithMultipleQueryData = withMultipleQueryData(PaymentDetailsContent);

export default PaymentDetails;
